/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { CountryCodeEnum } from 'features/common/types';
import { promoBanners, PromoBannerConfig } from 'constants/promoBanners';

interface Props {
	countryCode: CountryCodeEnum;
	isExternal?: boolean;
}

/**
 * Verifica si un banner está activo según su país, fechas y estado activo
 */
const isBannerActive = (banner: PromoBannerConfig, countryCode: CountryCodeEnum): boolean => {
	// Verificar si está activo
	if (!banner.active) {
		return false;
	}

	// Verificar si aplica para el país
	if (banner.countries !== 'ALL' && !banner.countries.includes(countryCode)) {
		return false;
	}

	// Verificar fechas si están configuradas
	if (banner.startDateTime && banner.endDateTime) {
		const now = new Date();
		let startDate = new Date(banner.startDateTime);
		let endDate = new Date(banner.endDateTime);

		// Si hay timezone configurado, interpretar las fechas como si fueran en ese timezone
		// y convertirlas a GMT para comparar con la hora actual
		if (banner.timezone) {
			// Extraer el offset del timezone (ej: 'GMT-4' -> -4)
			const timezoneMatch = banner.timezone.match(/GMT([+-]\d+)/);
			if (timezoneMatch) {
				const offsetHours = parseInt(timezoneMatch[1], 10);
				// Las fechas están escritas como si fueran en el timezone del banner
				// Convertirlas a GMT: si es GMT-4, sumar 4 horas para obtener GMT
				startDate = new Date(startDate.getTime() - offsetHours * 60 * 60 * 1000);
				endDate = new Date(endDate.getTime() - offsetHours * 60 * 60 * 1000);
			}
		}

		// Comparar la hora actual (en GMT) con las fechas convertidas
		return now >= startDate && now <= endDate;
	}

	return true;
};

/**
 * Obtiene el banner activo para un país específico
 * Si hay múltiples banners activos, retorna el más reciente (por createdAt)
 */
const getActiveBanner = (countryCode: CountryCodeEnum): PromoBannerConfig | null => {
	const activeBanners = promoBanners
		.filter((banner) => isBannerActive(banner, countryCode))
		.sort((a, b) => {
			// Ordenar por fecha de creación (más reciente primero)
			const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
			const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
			return dateB - dateA;
		});

	return activeBanners.length > 0 ? activeBanners[0] : null;
};

export const PromoBanner: React.FC<Props> = ({ countryCode, isExternal }) => {
	// Cuando isExternal es true, mostrar el banner de pago aunque la BU sea UY (usamos Paraguay para obtener el banner activo)
	const effectiveCountryCode = isExternal ? CountryCodeEnum.PARAGUAY : countryCode;
	const banner = useMemo(() => getActiveBanner(effectiveCountryCode), [effectiveCountryCode]);

	if (!banner) {
		return null;
	}

	return (
		<Box
			sx={{
				width: '100%',
				backgroundColor: banner.backgroundColor,
				padding: { xs: '12px 16px', sm: '16px 24px' },
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				flexWrap: { xs: 'wrap', sm: 'nowrap' },
				gap: { xs: '8px', sm: '16px' },
				minHeight: { xs: 'auto', sm: '60px' },
			}}
		>
			<Container
				maxWidth="md"
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					flexWrap: { xs: 'wrap', sm: 'nowrap' },
					gap: { xs: '8px', sm: '16px' },
					padding: 0,
				}}
			>
				{/* Logo a la izquierda (solo en desktop, en mobile puede ir arriba) */}
				{banner.logo && (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							order: { xs: 1, sm: 1 },
							flexShrink: 0,
							marginRight: 2,
						}}
					>
						<img
							src={banner.logo}
							alt="Logo promocional"
							style={{
								maxHeight: '40px',
								maxWidth: '120px',
								height: 'auto',
								width: 'auto',
								objectFit: 'contain',
							}}
						/>
					</Box>
				)}

				{/* Contenido de texto (título y descripción) */}
				<Box
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						gap: '4px',
						order: { xs: 3, sm: 2 },
						minWidth: 0, // Permite que el texto se ajuste
					}}
				>
					<Typography
						variant="h6"
						component="h2"
						sx={{
							fontSize: { xs: '1rem', sm: '1.1rem' },
							fontWeight: 'bold',
							color: '#FFFFFF',
							lineHeight: 1.2,
						}}
					>
						{banner.title}
					</Typography>
					<Typography
						variant="body2"
						component="p"
						sx={{
							fontSize: { xs: '0.875rem', sm: '0.9rem' },
							color: '#FFFFFF',
							lineHeight: 1.3,
							opacity: 0.95,
						}}
					>
						{banner.description}
					</Typography>
				</Box>

				{/* Imagen pequeña a la derecha (opcional) */}
				{banner.image && (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							order: { xs: 2, sm: 3 },
							flexShrink: 0,
						}}
					>
						<img
							src={banner.image}
							alt=""
							style={{
								maxHeight: '50px',
								maxWidth: '80px',
								height: 'auto',
								width: 'auto',
								objectFit: 'contain',
							}}
						/>
					</Box>
				)}
			</Container>
		</Box>
	);
};
